import styled from 'styled-components';
import {
  intervalPatternSets,
  intervalSetName,
  IntervalSetSessions,
  IntervalSetStatus,
  intervalSetStatuses,
  solfegeSyllables,
} from '../../../../../common/src/interval_training.ts';
import { learnLevelName } from '../../../../../common/src/learn_rules.ts';
import { LearnLevel } from '../../../../../common/src/model.ts';
import CheckIcon from '../../../assets/learn-check.svg?react';
import HouseIcon from '../../../assets/learn-house.svg?react';
import { PrimaryButton } from '../learn_buttons.tsx';
import { resultColorCss } from '../learn_colors.ts';
import { LevelChips } from '../learn_level_chips.tsx';
import { IconLine, ScreenBody, ScreenSubtitle, ScreenTitle, shortScreen, Slack } from '../learn_screen_parts.tsx';

// The Interval Training start screen (issue #3721), from the "Interval Training: start" artboard, in
// the shape of Pitch Match's: a line about the exercise with do, the home note, picked out in the
// accent colour, the shared level chips, then the checklist of the level's pattern sets - done
// (a green circle with a check), the one Start plays (filled with the accent) and the ones still
// to come (an empty circle, the name greyed) - the line saying the home note fits the singer's
// range, the link to Meet the syllables, and Start. Every row is a button too: a tap plays that
// set, done, current or still to come, so a singer can go back to sol – mi or jump ahead (the
// product owner, 2026-10-02); a greyed row is greyed for its status and is never disabled. Start
// and a row tap are each the user gesture that opens audio, so they are real buttons and nothing
// starts on its own.
//
// Presentational: which set is which comes from the sessions the singer has played on each set
// (intervalSetStatuses), which the page reads from their progress. A short screen (iPhone SE)
// tightens the checklist rather than scrolling, so Start stays on the screen with the locked
// levels' captions showing.

export const intervalStartScreenId = 'interval-start';
export const intervalStartButtonId = 'interval-start-button';
export const intervalSyllablesLinkId = 'interval-syllables-link';
export const intervalSetRowTestId = 'interval-set-row';

const homeSyllable = solfegeSyllables[0];
export const intervalStartTitle = 'Hear a pattern, sing it back';
const subtitleLead = 'Every pattern is sung from ';
const subtitleTail = ', the home note. Tap the house any time to hear where home is.';
export const intervalStartSubtitle = `${subtitleLead}${homeSyllable}${subtitleTail}`;
export const homeNoteSetText = 'Home note set for your range';
export const howToSaySyllablesText = 'How to say the syllables';

// "Easy patterns", the heading over the checklist
export function intervalSetsHeading(level: LearnLevel): string {
  return `${learnLevelName(level)} patterns`;
}

// The status a row's mark shows, in words, for its button's accessible name
export const setStatusLabels: Record<IntervalSetStatus, string> = {
  done: 'done',
  current: 'working on now',
  upcoming: 'still to come',
};

// "Play sol – mi, done": what a tap on the row does, and the status its mark shows
export function intervalSetButtonLabel(name: string, status: IntervalSetStatus): string {
  return `Play ${name}, ${setStatusLabels[status]}`;
}

export const IntervalStartScreen = ({
  level,
  unlocked,
  selectable,
  setSessions,
  onSelectLevel,
  onOpenSyllables,
  onStart,
  onStartSet,
}: {
  level: LearnLevel;
  unlocked: LearnLevel[];
  selectable: LearnLevel[];
  setSessions: IntervalSetSessions; // sessions played on each of the level's sets, which decide the checklist
  onSelectLevel: (level: LearnLevel) => void;
  onOpenSyllables: () => void; // "How to say the syllables"
  onStart: () => void;
  onStartSet: (index: number) => void; // a row tapped: a session on that set
}) => {
  const sets = intervalPatternSets(level);
  const statuses = intervalSetStatuses(level, setSessions);
  return (
    <ScreenBody id={intervalStartScreenId}>
      <ScreenTitle>{intervalStartTitle}</ScreenTitle>
      <ScreenSubtitle>
        {subtitleLead}
        <HomeWord>{homeSyllable}</HomeWord>
        {subtitleTail}
      </ScreenSubtitle>
      <LevelChips level={level} unlocked={unlocked} selectable={selectable} onSelectLevel={onSelectLevel} />
      <SetsHeading>{intervalSetsHeading(level)}</SetsHeading>
      <Checklist>
        {sets.map((set, index) => {
          const status = statuses[index];
          const name = intervalSetName(set);
          return (
            <li key={name}>
              <SetRow
                type="button"
                data-testid={intervalSetRowTestId}
                data-index={index}
                data-status={status}
                aria-label={intervalSetButtonLabel(name, status)}
                aria-current={status === 'current' ? 'step' : undefined}
                onClick={() => onStartSet(index)}
              >
                <Mark $status={status} aria-hidden="true">
                  {status === 'done' && <CheckIcon />}
                </Mark>
                <SetText>
                  <SetName $status={status}>{name}</SetName>
                  <SetDescription>{set.description}</SetDescription>
                </SetText>
              </SetRow>
            </li>
          );
        })}
      </Checklist>
      <HomeLine>
        <HouseIcon aria-hidden="true" />
        <span>{homeNoteSetText}</span>
      </HomeLine>
      <SyllablesLink
        id={intervalSyllablesLinkId}
        data-testid={intervalSyllablesLinkId}
        type="button"
        onClick={onOpenSyllables}
      >
        {howToSaySyllablesText}
      </SyllablesLink>
      <Slack />
      <PrimaryButton id={intervalStartButtonId} data-testid={intervalStartButtonId} onClick={onStart}>
        Start
      </PrimaryButton>
    </ScreenBody>
  );
};

const HomeWord = styled.span`
  font-family: Jost-SemiBold, Arial, sans-serif;
  color: var(--color-tab-selected);
`;

const SetsHeading = styled.div`
  margin-top: 18px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 15px;
  line-height: 22px;

  ${shortScreen} {
    margin-top: 10px;
  }
`;

const Checklist = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

// A real button laid out as the artboard's row: full width, no fill, the divider under it. At least
// minTapPx tall on every screen (the text alone makes it 49 px on a short one). Pressed, it takes
// the soft grey the Meet the syllables rungs and the class screen's tiles are filled with.
const minTapPx = 44;

const SetRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  width: 100%;
  min-height: ${minTapPx}px;
  margin: 0;
  padding: 10px 0;
  border: none;
  border-bottom: 1px solid var(--color-divider);
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active {
    background-color: var(--color-background-search);
  }

  &:focus-visible {
    outline: 2px solid var(--color-tab-selected);
    outline-offset: -2px;
  }

  ${shortScreen} {
    padding: 5px 0;
  }
`;

const markSize = 24;

// Done: green with a white check; current: the accent; still to come: an empty ring
const markFill: Record<IntervalSetStatus, string> = {
  done: resultColorCss.green,
  current: 'var(--color-tab-selected)',
  upcoming: 'transparent',
};

const markBorder: Record<IntervalSetStatus, string> = {
  done: resultColorCss.green,
  current: 'var(--color-tab-selected)',
  upcoming: 'var(--color-divider)',
};

const Mark = styled.span<{ $status: IntervalSetStatus }>`
  flex: none;
  box-sizing: border-box;
  width: ${markSize}px;
  height: ${markSize}px;
  border-radius: 50%;
  border: 2px solid ${({ $status }) => markBorder[$status]};
  background-color: ${({ $status }) => markFill[$status]};
  color: var(--color-text-white);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const SetText = styled.div`
  flex: auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const SetName = styled.span<{ $status: IntervalSetStatus }>`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 16px;
  line-height: 24px;
  // Still to come is greyed, in the grey the descriptions use rather than the disabled grey: the row
  // can still be tapped, so its name has to stay readable
  color: ${({ $status }) => ($status === 'upcoming' ? 'var(--color-text-darker)' : 'var(--color-text)')};

  ${shortScreen} {
    line-height: 22px;
  }
`;

const SetDescription = styled.span`
  font-size: 13px;
  line-height: 19px;
  color: var(--color-text-darker);

  ${shortScreen} {
    line-height: 17px;
  }
`;

const HomeLine = styled(IconLine)`
  margin-top: 8px;
`;

// The artboard draws an underlined link; it opens a screen in the app, so it is a button. Sing in
// Solfège's start screen draws the same link (issue #3722).
export const SyllablesLink = styled.button`
  align-self: flex-start;
  margin-top: 6px;
  padding: 4px 0;
  border: none;
  background: none;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: var(--color-tab-selected);
  text-decoration: underline;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  ${shortScreen} {
    margin-top: 2px;
  }
`;
